/**
 * Copy, in both languages (§30).
 *
 * §61 lists "Postpone English/Hinglish until the end" as something the
 * frontend engineer must NOT do, so this file exists before the first screen
 * and every string goes through it. There is no English-only path to fall
 * back into later.
 *
 * Hinglish here is written the way an Indian tutor actually speaks — Hindi
 * and English mixed in Roman script — not a word-for-word translation. §30:
 * "Hinglish should be natural Roman-script conversation, not word-for-word
 * translation." Maths terms stay in English on purpose: a child who learns
 * "LCM" in class needs to hear "LCM", not a Hindi coinage they will never
 * see on a worksheet.
 */

export const LANGUAGES = ['en', 'hinglish'] as const;
export type Language = (typeof LANGUAGES)[number];

export const DEFAULT_LANGUAGE: Language = 'en';

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

type Dict = Record<string, string>;

export const en = {
  // --- app shell -----------------------------------------------------------
  'app.name': 'PrepLift',
  'app.skipToContent': 'Skip to main content',
  'nav.home': 'Home',
  'nav.learn': 'Learn',
  'nav.practice': 'Practice',
  'nav.profile': 'Profile',
  'nav.label': 'Main',

  // --- greetings -----------------------------------------------------------
  'home.greeting.morning': 'Good morning, {name}',
  'home.greeting.afternoon': 'Good afternoon, {name}',
  'home.greeting.evening': 'Good evening, {name}',
  'home.greeting.anon': 'Hello there',

  // --- home ----------------------------------------------------------------
  'home.homework.title': 'Homework Help',
  'home.homework.subtitle': 'Ask PrepLift anything from your homework.',
  'home.continue.title': 'Continue Learning',
  'home.practice.title': 'Practice',
  'home.practice.subtitle': '{count} questions ready for you.',
  'home.practice.subtitle.zero': 'Pick a subject and try a few questions.',
  'home.empty': "Let's get started! Ask me about your homework.",
  'home.streak': '{count} day streak',
  'home.xp': '{count} XP',

  // --- homework ------------------------------------------------------------
  'homework.title': 'Homework Help',
  'homework.prompt': 'What do you need help with?',
  'homework.placeholder': 'Type your question...',
  'homework.send': 'Send',
  'homework.photoHint': 'You can also take a photo.',
  'homework.addPhoto': 'Add a photo',
  'homework.recordVoice': 'Record your question',
  'homework.removePhoto': 'Remove photo',
  'homework.retakePhoto': 'Choose a different photo',
  'homework.recording': 'Recording...',
  'homework.stopRecording': 'Stop',
  'homework.cancelRecording': 'Cancel',
  'homework.playback': 'Listen to your recording',
  'homework.micDenied': 'Microphone access is off. You can type instead.',
  'homework.micUnsupported': "This browser can't record audio. You can type or add a photo.",
  'homework.cameraUnsupported': 'Choose a photo from your device.',

  // --- processing (§17) ----------------------------------------------------
  'homework.state.uploading': 'Sending your question...',
  'homework.state.extracting': 'Reading your question...',
  'homework.state.classifying': 'Working out what this is about...',
  'homework.state.generating': 'Thinking about how to explain it...',
  'homework.state.wait': 'This takes a few seconds. You can wait here.',
  'homework.state.failed': "I couldn't finish that one yet. Let's try again.",

  // --- tutor response ------------------------------------------------------
  'tutor.heading': "Here's how we solve it",
  'tutor.step': 'Step {n}',
  'tutor.hint': 'Hint',
  'tutor.check': 'Quick check',
  'tutor.practiceCta': 'Try a similar question',
  'tutor.understood': 'I understand',
  // Says what happens next. A button that silently produces a test is one
  // children learn not to press.
  'tutor.readyForQuestion': "I've got it — ask me the question",
  'tutor.done': 'Done for now',
  'tutor.skip': 'Skip this question',
  'tutor.needHelp': 'Need more help',
  'error.TOKEN_INVALID': "You've been signed out. Please sign in again.",
  'tutor.clarify': 'One quick question first',
  // The formula block. "Rule" rather than "Formula" because a nine-year-old
  // meets the word rule years before they meet the word formula.
  'tutor.rule': 'Rule',
  'tutor.ruleExample': 'For example',
  'tutor.uncertain': "I'm not fully sure about this one — check with your teacher too.",

  // The question picker (§13.5b). A photo of an exercise page holds twenty
  // problems, and the tutor can only help with one at a time.
  //
  // The heading says what it found before it asks anything, because a child
  // who tapped "send" and got a question back needs to know nothing has gone
  // wrong — their photo worked, there is just more than one thing on it.
  'picker.heading': 'I can see a few questions on this page',
  'picker.chooseNumber': 'Which one are you stuck on?',
  'picker.choosePart': 'Which part?',
  'picker.questionLabel': 'Question {number}',
  'picker.partLabel': 'Part ({part})',
  'picker.partCount': '{count} parts to choose from',
  'picker.back': 'Pick a different question',
  'picker.nextFromPage': 'Another question from this page',
  // Shown above the steps when the tutor read what the child already wrote.
  // Deliberately "I looked at your working" and not "you made a mistake":
  // the child's page is being taken seriously, which is the whole point of
  // reading it.
  'tutor.readYourWork': 'I looked at what you wrote',

  // The answer gate. The tone is doing real work here: the child is being
  // asked to finish the question, not being tested, and the wording has to
  // make that difference obvious at eight years old.
  'tutor.readAloud': 'Read it to me',
  'tutor.preparingAudio': 'Getting it ready…',
  'tutor.yourTurn': 'Your turn',
  'tutor.answerPlaceholder': 'Write your answer',
  'tutor.answerSubmit': 'Check my answer',
  'tutor.dontKnow': "I don't know",
  'tutor.showMe': 'Show me how',
  'tutor.correct': "That's right!",
  'tutor.notQuite': 'Not quite — look at the working.',
  'tutor.hereIsHow': "Here's how it works out",
  'tutor.answerWas': 'The answer is {value}',
  'tutor.checked': 'Checked',

  // Doubts.
  'doubt.heading': 'Ask me anything about this',
  'doubt.youSent': 'You sent a photo',
  'doubt.marked.right': '{answer} is right',
  'doubt.marked.wrong': '{answer} isn’t right',
  'doubt.placeholder': 'Ask anything',
  'doubt.placeholderAgain': 'Ask anything',
  'doubt.send': 'Ask',
  'doubt.thinking': 'Let me explain it another way…',
  'doubt.failed': "I couldn't answer that just now. Try again?",
  'doubt.enough': "We've been round this a few times. Try a practice question instead?",

  // --- voice ---------------------------------------------------------------
  'voice.label': 'Listen',
  'voice.play': 'Play explanation',
  'voice.pause': 'Pause',
  'voice.restart': 'Start again',
  'voice.loading': 'Getting the audio ready...',
  'voice.unavailable': 'Audio is not ready for this one. The steps above have everything.',
  'voice.speed': 'Speed',

  // --- memory recall (§20) -------------------------------------------------
  'recall.intro': 'Remember when we practised {topic}?',
  'recall.question': 'What did we do first?',
  'recall.forgot': 'I forgot',
  'recall.hint': 'Give me a hint',
  'recall.correct': "That's it! You remembered.",
  'recall.gentle': "No problem — let's go through it together.",

  // --- practice ------------------------------------------------------------
  'practice.title': 'Practice',
  'practice.prompt': 'What do you want to practise?',
  'practice.mixed': 'Mixed Practice',
  'practice.recommended': 'Recommended for me',
  'practice.questionOf': 'Question {n} of {total}',
  'practice.answerPlaceholder': 'Type your answer',
  'practice.preparing': 'Making your questions...',
  'billing.trialEndingTitle': 'Your free trial is nearly over',
  'billing.trialEndingBody': '{days} days left. Keep PrepLift going for your child.',
  'billing.trialEndedTitle': 'Your free trial has ended',
  'billing.trialEndedBody': 'Subscribe to carry on getting homework help.',
  'billing.noPlanTitle': "We can't find your plan",
  'billing.noPlanBody':
    "This one is on us, not you. If you signed up recently your free trial should still be running - take a look at the plans, or wait a little and try again.",
  'billing.subscribe': 'See plans',
  'billing.plansTitle': 'Choose a plan',
  'billing.perMonth': 'per month',
  'billing.perSixMonths': 'every 6 months',
  'billing.perYear': 'per year',
  'billing.oneChild': 'For 1 child',
  'billing.twoChildren': 'For 2 children',
  'billing.forChildren': 'For {count} children',
  'billing.biggerFamily':
    'You have {children} children on this account. The largest plan covers {covers} — tell us about the others and we will sort it out.',
  'billing.choose': 'Choose',
  'billing.pinTitle': 'Enter your PIN',
  'billing.pinBody': 'Only a parent can pay, so this asks for the PIN first.',
  'billing.opening': 'Opening payment...',
  'billing.confirming': 'Confirming your payment...',
  'billing.active': "You're subscribed. Thank you!",
  'billing.pendingNote': 'If the money has left your account and this is still here, give it a minute and refresh — we wait for the bank to confirm.',
  'billing.cancelled': 'Payment was not completed. Nothing has been charged.',
  'practice.showWorking': 'Show me what you did',
  'practice.readingWorking': 'Looking at your working...',
  'practice.hearingDoubt': 'Listening to you...',
  'practice.nextStep': 'Try this next',
  'practice.workingUnreadable': "That one didn't come through. Try again?",
  'practice.check': 'Check',
  'practice.checking': 'Checking...',
  'practice.next': 'Next question',
  'practice.tryAgain': 'Try again',
  'practice.showMe': 'Show me',
  'practice.correct': 'Great try!',
  'practice.incorrect': 'Almost there!',
  'practice.yourAnswer': 'Your answer',
  'practice.correctAnswer': 'Correct answer',
  'practice.complete': 'Practice complete!',
  'practice.summary': '{total} questions • {correct} correct',
  'practice.again': 'Practice again',
  'practice.learnMore': 'Learn more',
  'practice.home': 'Home',
  'practice.empty': 'Choose a subject or try your recommended practice.',
  'practice.xpEarned': '+{count} XP',

  // --- learn ---------------------------------------------------------------
  'learn.title': 'Learn',
  'learn.prompt': 'Pick a subject and let’s learn something new.',
  'learn.recommended': 'Recommended for you',
  'learn.popular': 'Popular topics',
  'learn.lessonCta': 'Try {count} questions',
  // --- asking about a topic (§22) -------------------------------------------
  // The starters are questions a child taps rather than types. Phrased as a
  // child would ask them, because they are sent verbatim as the question.
  'learn.chat.heading': 'Not sure about this? Ask me',
  'learn.chat.placeholder': 'Ask anything',
  'learn.chat.starterMeaning': 'What does this mean?',
  'learn.chat.starterExample': 'Give me an example',
  'learn.chat.starterRemember': 'How do I remember this?',
  'learn.chat.openHomework': 'Open Homework Help for this question',
  'learn.done': 'Done',
  'learn.empty': 'Pick a subject and let’s learn something new.',

  // --- mastery, in words not numbers (§27) ---------------------------------
  'mastery.not_learned': "Let's learn this",
  'mastery.learning': "You're learning this",
  'mastery.practicing': "You're getting stronger",
  'mastery.mastered': 'You know this well!',
  'mastery.retention_review': "Let's review this again",
  'mastery.none': 'Not started yet',

  // --- progress ------------------------------------------------------------
  'progress.title': 'My progress',
  'progress.empty': 'Your learning journey will appear here as you practise.',
  'progress.skillsStrong': 'Going well',
  'progress.skillsPractice': 'Worth more practice',

  // --- profile -------------------------------------------------------------
  'profile.title': 'Profile',
  'profile.class': 'Class',
  'profile.board': 'Board',
  'profile.language': 'Language',
  'profile.myProgress': 'My progress',
  'profile.account': 'Account',
  'profile.signOut': 'Sign out',
  'profile.signOutConfirm': 'Sign out of PrepLift?',
  'profile.changeClassWarning':
    'Changing your class changes the questions and lessons you get. Are you sure?',
  'profile.confirm': 'Yes, change it',
  'profile.cancel': 'Cancel',
  'profile.saved': 'Saved',

  // --- language ------------------------------------------------------------
  'language.en': 'English',
  'language.hinglish': 'Hinglish',
  'language.choose': 'Choose your language',

  // --- auth ----------------------------------------------------------------
  'auth.welcome': 'Welcome to PrepLift',
  'auth.phonePlaceholder': '+91 98765 43210',
  'auth.sendLink': 'Send me a link',
  'auth.sending': 'Sending...',
  'auth.linkSent': 'Check your WhatsApp',
  'auth.linkSentBody': 'We sent you a link to sign in. It works once and expires in 10 minutes.',
  'auth.signingIn': 'Signing you in...',
  'auth.expired': "This login link has expired. Let's get you a new one.",
  'auth.tryAgain': 'Get a new link',

  // --- parent --------------------------------------------------------------
  'parent.title': "Your child's learning",
  'parent.thisWeek': "Your child's learning this week",
  'parent.homeworkCount': 'Homework: {count} questions',
  'parent.practiceCount': 'Practice: {count} questions',
  'parent.improving': 'Improving',
  'parent.needsPractice': 'Needs more practice',
  'parent.activity': 'Activity: {count} days this week',
  'parent.empty': 'Progress will appear after your child starts learning.',
  'parent.selectChild': 'Choose a child',

  // --- parent-first onboarding (§17, §32) ----------------------------------
  // The parent is the account holder: they sign up, create each child, and
  // hand the phone over. The copy is written for an adult, plainly, and never
  // explains the mechanism -- a parent does not need to know what a token is.
  'profiles.heading': 'Who’s learning?',
  'profiles.parentTile': 'Continue as parent',
  'profiles.parentSubtitle': 'Progress, settings and adding a child',
  'profiles.addChild': 'Add a child',
  'profiles.switch': 'Switch profile',
  'profiles.empty': 'Add your first child to get started.',
  'profiles.classLine': 'Class {class} · {board}',
  'auth.parentPhoneLabel': 'Your WhatsApp number',
  'auth.parentTagline': 'Set up your child in two minutes.',
  'parent.thisChild': 'your child',
  'parent.link.heading': 'Opening PrepLift on their phone',
  "parent.link.none": "Send a WhatsApp link that opens {name}'s PrepLift. No number to type, and it keeps working.",
  'parent.link.send': 'Send the link on WhatsApp',
  'parent.link.sending': 'Sending...',
  'parent.link.live': 'Link sent. It works until {date} and stays live as long as {name} keeps using it.',
  'parent.link.used': 'Used {count} times',
  'parent.link.resend': 'Send a new link',
  'parent.link.revoke': 'Stop the link working',
  'parent.link.revoked': 'That link no longer works. Send a new one when you need it.',
  'parent.link.failed': 'We could not reach WhatsApp just now. Try sending it again.',
  "parent.link.caution": "Anyone who opens that WhatsApp message can do {name}'s homework. It cannot see your billing, reports or settings, and you can stop it working at any time.",
  'auth.recalledLabel': 'Sign in as',
  'auth.recalledSend': 'Send my link',
  'auth.notMe': 'Use a different number',
  'parent.setup.title': 'Set up your account',
  'parent.setup.nameLabel': 'Your name',
  'parent.setup.namePlaceholder': 'e.g. Priya',
  'parent.setup.pinTitle': 'Choose a parent PIN',
  'parent.setup.pinWhy':
    'Your child uses this phone to study. The PIN is how you come back to your own screen.',
  'parent.setup.pinLabel': 'PIN ({min}-{max} digits)',
  'parent.setup.pinConfirmLabel': 'Enter it again',
  'parent.setup.pinMismatch': "Those two PINs don't match.",
  'parent.setup.save': 'Save and continue',
  'parent.addChild.title': 'Add your child',
  'parent.addChild.nameLabel': "Your child's name",
  'parent.addChild.namePlaceholder': 'e.g. Aarav',
  'parent.addChild.classLabel': 'Class',
  'parent.addChild.boardLabel': 'Board',
  'parent.addChild.languageLabel': 'Language for lessons',
  'parent.addChild.consent':
    'You confirm you are this child’s parent or guardian and agree to PrepLift processing their learning data.',
  'parent.addChild.save': 'Add child',
  'parent.addChild.another': 'Add another child',
  'parent.children.title': 'Your children',
  'parent.children.empty': 'Add your child to get started.',
  'parent.children.startPractice': 'Hand over the phone',
  'parent.children.ownLogin': 'Signs in with their own number on their own phone.',
  'parent.children.openApp': 'Open {name}’s app',
  // A child always has a name in practice -- the add-child form requires
  // one -- but the API type allows null, and "Open —'s app" is the kind of
  // thing that reaches a parent exactly once and is remembered.
  'parent.children.openAppUnnamed': 'Open their app',
  'parent.children.seeProgress': 'See progress',
  'parent.handoff.confirmTitle': 'Give the phone to {name}?',
  'parent.handoff.confirmBody':
    'You will be signed out here. Tap "Parent zone" and enter your PIN to come back.',
  'parent.handoff.confirm': 'Hand over',
  'parent.handoff.needsPin': 'Set your parent PIN first, so you can get back.',
  'parent.zone': 'Parent zone',
  'parent.section.title': 'For a parent',
  'parent.section.changePin': 'Change the PIN',
  "start.title": "Who's using the phone?",
  'start.whose': 'Tap a name to open their PrepLift.',
  'start.open': 'Open',
  'pin.label': 'Parent PIN',
  'pin.body': 'Ask a parent to enter the PIN.',
  'pin.submit': 'Continue',
  'pin.noneSet': 'No PIN has been set on this account yet. Sign in with WhatsApp to set one.',
  'parent.unlock.title': 'Enter your PIN',
  'parent.unlock.body': 'This takes you back to the parent screen.',
  'parent.unlock.submit': 'Unlock',
  'parent.unlock.attempts': '{count} tries left',
  'parent.unlock.signOutInstead': 'Sign in with WhatsApp instead',
  'child.practisingAs': "You're practising as {name}",

  // --- errors (§38's table) ------------------------------------------------
  'error.OFFLINE': "Looks like you're offline. Check your internet and try again.",
  'error.NETWORK_ERROR': "Looks like you're offline. Check your internet and try again.",
  'error.TIMEOUT': 'That took too long. Let’s try again.',
  'error.AI_UNAVAILABLE': "I couldn't finish that one yet. Let's try again.",
  'error.AI_SCHEMA_REJECTED': "I couldn't finish that one yet. Let's try again.",
  'error.AI_SAFETY_REJECTED': "I can't help with that one. Try a homework question.",
  'error.AI_QUOTA_EXCEEDED': "You've done a lot today! Try again later.",
  'error.HOMEWORK_PROCESSING_FAILED': "I couldn't finish that one yet. Let's try again.",
  'error.PAYLOAD_TOO_LARGE': 'That photo is too big. Try a smaller one.',
  // Raised for voice recordings as well as photographs, so it cannot say
  // "photo": a child who had recorded a question on an Android phone was told
  // this kind of photo was unsupported, having taken none.
  'error.UNSUPPORTED_MEDIA_TYPE': "We can't open that one. Try again?",
  'error.TOKEN_EXPIRED': "This login link has expired. Let's get you a new one.",
  'error.TOKEN_ALREADY_USED': "This login link was already used. Let's get you a new one.",
  'error.SESSION_REVOKED': "You've been signed out. Let's sign you back in.",
  'error.AUTHENTICATION_REQUIRED': 'Please sign in to continue.',
  'error.INVALID_CREDENTIALS': "That link didn't work. Let's get you a new one.",
  'error.PERMISSION_DENIED': "You don't have access to that.",
  // The trial ended. Read by a child at least as often as by a parent, so it
  // says what happened and who can fix it without implying the child did
  // something wrong.
  'error.SUBSCRIPTION_REQUIRED': 'Your free trial has ended. Ask a parent to continue your plan.',
  'error.PIN_INCORRECT': "That PIN isn't right.",
  'error.PIN_LOCKED': 'Too many tries. Please wait a few minutes and try again.',
  'error.PIN_TOO_WEAK': 'Please choose a less obvious PIN.',
  'error.RESOURCE_NOT_FOUND': "We couldn't find that.",
  'error.RATE_LIMITED': "That's a bit too fast. Take a breath and try again.",
  'error.CURRICULUM_NOT_AVAILABLE': "We don't have that syllabus ready yet.",
  'error.PRACTICE_SESSION_CLOSED': "That practice set is already finished.",
  'error.MALFORMED_RESPONSE': 'Something went wrong. Please try again.',
  'error.unknown': 'Something went wrong. Please try again.',
  'error.retry': 'Try again',
  'error.goHome': 'Go home',
  'error.uploadFailed': "The photo didn't upload. Try again.",
  'error.notFoundTitle': "We couldn't find that page",
  'error.notFoundBody': "Let's get you back to somewhere useful.",

  // --- legal ---------------------------------------------------------------
  // The documents themselves are English only, and deliberately: a privacy
  // policy that exists in two languages has to say which one governs, and an
  // answer to that question is worth less than one unambiguous document. The
  // chrome around them is translated, because finding the page should not
  // require reading English first.
  'legal.privacy': 'Privacy Policy',
  'legal.terms': 'Terms of Use',
  'legal.refunds': 'Cancellation and Refunds',
  'legal.effective': 'In effect from {date}',
  'legal.otherDocuments': 'Other policies',
  'legal.backToApp': 'Back to PrepLift',

  // --- generic -------------------------------------------------------------
  'common.loading': 'Loading...',
  'common.back': 'Back',
  'common.close': 'Close',
  'common.continue': 'Continue',
  'common.cancel': 'Cancel',

  // --- pwa -----------------------------------------------------------------
  'pwa.installTitle': 'Add PrepLift to your home screen',
  'pwa.installBody': 'Open it like an app, without the browser.',
  'pwa.install': 'Add',
  'pwa.notNow': 'Not now',
  'pwa.updateTitle': 'A new version is ready',
  'pwa.update': 'Refresh',
} satisfies Dict;

export type MessageKey = keyof typeof en;

export const hinglish: Record<MessageKey, string> = {
  'app.name': 'PrepLift',
  'app.skipToContent': 'Main content par jao',
  'nav.home': 'Home',
  'nav.learn': 'Seekho',
  'nav.practice': 'Practice',
  'nav.profile': 'Profile',
  'nav.label': 'Main',

  'home.greeting.morning': 'Good morning, {name}',
  'home.greeting.afternoon': 'Good afternoon, {name}',
  'home.greeting.evening': 'Good evening, {name}',
  'home.greeting.anon': 'Hello',

  'home.homework.title': 'Homework Help',
  'home.homework.subtitle': 'Homework ka koi bhi sawaal poocho.',
  'home.continue.title': 'Aage badho',
  'home.practice.title': 'Practice',
  'home.practice.subtitle': '{count} sawaal tumhare liye ready hain.',
  'home.practice.subtitle.zero': 'Koi subject chuno aur kuch sawaal try karo.',
  'home.empty': 'Chalo shuru karte hain! Homework ke baare mein poocho.',
  'home.streak': '{count} din ki streak',
  'home.xp': '{count} XP',

  'homework.title': 'Homework Help',
  'homework.prompt': 'Kis cheez mein help chahiye?',
  'homework.placeholder': 'Apna sawaal likho...',
  'homework.send': 'Bhejo',
  'homework.photoHint': 'Photo bhi le sakte ho.',
  'homework.addPhoto': 'Photo add karo',
  'homework.recordVoice': 'Sawaal bolkar record karo',
  'homework.removePhoto': 'Photo hatao',
  'homework.retakePhoto': 'Dusri photo chuno',
  'homework.recording': 'Record ho raha hai...',
  'homework.stopRecording': 'Stop',
  'homework.cancelRecording': 'Cancel',
  'homework.playback': 'Apni recording suno',
  'homework.micDenied': 'Microphone band hai. Tum likh bhi sakte ho.',
  'homework.micUnsupported': 'Is browser mein recording nahi hoti. Likho ya photo add karo.',
  'homework.cameraUnsupported': 'Apne device se photo chuno.',

  'homework.state.uploading': 'Tumhara sawaal bheja ja raha hai...',
  'homework.state.extracting': 'Tumhara sawaal padh rahe hain...',
  'homework.state.classifying': 'Dekh rahe hain ye kis topic ka hai...',
  'homework.state.generating': 'Soch rahe hain kaise samjhayein...',
  'homework.state.wait': 'Thode second lagenge. Yahin ruko.',
  'homework.state.failed': 'Abhi complete nahi kar paaye. Chalo phir se try karte hain.',

  'tutor.heading': 'Chalo aise solve karte hain',
  'tutor.step': 'Step {n}',
  'tutor.hint': 'Hint',
  'tutor.check': 'Ek chhota check',
  'tutor.practiceCta': 'Aisa hi ek sawaal try karo',
  'tutor.understood': 'Samajh gaya',
  'tutor.readyForQuestion': 'Samajh gaya — ab sawaal poocho',
  'tutor.done': 'Abhi ke liye ho gaya',
  'tutor.skip': 'Ye sawaal chhod do',
  'tutor.needHelp': 'Aur help chahiye',
  'error.TOKEN_INVALID': 'Aap sign out ho gaye ho. Dobara sign in karo.',
  'tutor.clarify': 'Pehle ek chhota sawaal',
  'tutor.rule': 'Rule',
  'tutor.ruleExample': 'Jaise',
  'tutor.uncertain': 'Is baare mein main poori tarah sure nahi hoon — teacher se bhi pooch lena.',

  'picker.heading': 'Is page par mujhe kuch sawaal dikh rahe hain',
  'picker.chooseNumber': 'Tum kis mein atke ho?',
  'picker.choosePart': 'Kaunsa part?',
  'picker.questionLabel': 'Sawaal {number}',
  'picker.partLabel': 'Part ({part})',
  'picker.partCount': '{count} part hain, kaunsa karna hai?',
  'picker.back': 'Doosra sawaal chuno',
  'picker.nextFromPage': 'Isi page se ek aur sawaal',
  'tutor.readYourWork': 'Tumne jo likha hai, wo maine dekha',

  'tutor.readAloud': 'Padh ke sunao',
  'tutor.preparingAudio': 'Taiyaar kar raha hoon…',
  'tutor.yourTurn': 'Ab tumhari baari',
  'tutor.answerPlaceholder': 'Apna jawab likho',
  'tutor.answerSubmit': 'Jawab check karo',
  'tutor.dontKnow': 'Nahi pata',
  'tutor.showMe': 'Dikha do kaise',
  'tutor.correct': 'Bilkul sahi!',
  'tutor.notQuite': 'Thoda miss ho gaya — working dekho.',
  'tutor.hereIsHow': 'Aise banta hai',
  'tutor.answerWas': 'Jawab hai {value}',
  'tutor.checked': 'Check ho gaya',

  'doubt.heading': 'Is sawaal ke baare mein kuch bhi poocho',
  'doubt.youSent': 'Tumne photo bheji',
  'doubt.marked.right': '{answer} sahi hai',
  'doubt.marked.wrong': '{answer} sahi nahi hai',
  'doubt.placeholder': 'Kuch bhi poocho',
  'doubt.placeholderAgain': 'Kuch bhi poocho',
  'doubt.send': 'Poocho',
  'doubt.thinking': 'Doosre tarike se samjhata hoon…',
  'doubt.failed': 'Abhi jawab nahi de paaya. Phir se try karo?',
  'doubt.enough': 'Kaafi baar dekh liya. Ek practice sawaal try karein?',

  'voice.label': 'Suno',
  'voice.play': 'Explanation suno',
  'voice.pause': 'Pause',
  'voice.restart': 'Phir se shuru',
  'voice.loading': 'Audio ready ho raha hai...',
  'voice.unavailable': 'Iska audio ready nahi hai. Upar ke steps mein sab kuch hai.',
  'voice.speed': 'Speed',

  'recall.intro': 'Yaad hai humne {topic} practice kiya tha?',
  'recall.question': 'Sabse pehle humne kya kiya tha?',
  'recall.forgot': 'Bhool gaya',
  'recall.hint': 'Ek hint do',
  'recall.correct': 'Bilkul sahi! Tumhe yaad tha.',
  'recall.gentle': 'Koi baat nahi — chalo saath mein dekhte hain.',

  'practice.title': 'Practice',
  'practice.prompt': 'Kya practice karna hai?',
  'practice.mixed': 'Mixed Practice',
  'practice.recommended': 'Mere liye recommended',
  'practice.questionOf': 'Sawaal {n} of {total}',
  'practice.answerPlaceholder': 'Apna jawab likho',
  'practice.preparing': 'Tumhare sawaal ban rahe hain...',
  'billing.trialEndingTitle': 'Free trial khatam hone wala hai',
  'billing.trialEndingBody': '{days} din bache hain. PrepLift chalu rakho apne bachche ke liye.',
  'billing.trialEndedTitle': 'Free trial khatam ho gaya',
  'billing.trialEndedBody': 'Homework help jaari rakhne ke liye subscribe karo.',
  'billing.noPlanTitle': 'Tumhara plan nahi mil raha',
  'billing.noPlanBody':
    'Ye galti humari hai, tumhari nahi. Abhi signup kiya hai to free trial chalu hona chahiye - plans dekh lo, ya thodi der baad phir se try karo.',
  'billing.subscribe': 'Plans dekho',
  'billing.plansTitle': 'Ek plan chuno',
  'billing.perMonth': 'har mahine',
  'billing.perSixMonths': 'har 6 mahine',
  'billing.perYear': 'har saal',
  'billing.oneChild': '1 bachche ke liye',
  'billing.twoChildren': '2 bachchon ke liye',
  'billing.forChildren': '{count} bachchon ke liye',
  'billing.biggerFamily':
    'Is account par {children} bachche hain. Sabse bada plan {covers} ke liye hai — baaki bachchon ke liye humein batao.',
  'billing.choose': 'Chuno',
  'billing.pinTitle': 'Apna PIN daalo',
  'billing.pinBody': 'Payment sirf parent kar sakte hain, isliye pehle PIN poochha jaata hai.',
  'billing.opening': 'Payment khul raha hai...',
  'billing.confirming': 'Payment confirm ho raha hai...',
  'billing.active': 'Subscribe ho gaya. Shukriya!',
  'billing.pendingNote': 'Paise kat gaye hain aur ye abhi bhi dikh raha hai? Ek minute ruk kar refresh karo — hum bank ke confirm karne ka intezaar karte hain.',
  'billing.cancelled': 'Payment poora nahi hua. Koi paisa nahi kata.',
  'practice.showWorking': 'Apna kaam dikhao',
  'practice.readingWorking': 'Tumhara kaam dekh rahe hain...',
  'practice.hearingDoubt': 'Tumhari baat sun rahe hain...',
  'practice.nextStep': 'Ab ye karo',
  'practice.workingUnreadable': 'Ye samajh nahi paaye. Phir se try karo?',
  'practice.check': 'Check karo',
  'practice.checking': 'Check ho raha hai...',
  'practice.next': 'Agla sawaal',
  'practice.tryAgain': 'Phir se try karo',
  'practice.showMe': 'Mujhe dikhao',
  'practice.correct': 'Shabaash!',
  'practice.incorrect': 'Bas thoda sa reh gaya!',
  'practice.yourAnswer': 'Tumhara jawab',
  'practice.correctAnswer': 'Sahi jawab',
  'practice.complete': 'Practice complete!',
  'practice.summary': '{total} sawaal • {correct} sahi',
  'practice.again': 'Phir se practice',
  'practice.learnMore': 'Aur seekho',
  'practice.home': 'Home',
  'practice.empty': 'Koi subject chuno ya recommended practice try karo.',
  'practice.xpEarned': '+{count} XP',

  'learn.title': 'Seekho',
  'learn.prompt': 'Koi subject chuno aur kuch naya seekhte hain.',
  'learn.recommended': 'Tumhare liye recommended',
  'learn.popular': 'Popular topics',
  'learn.lessonCta': '{count} sawaal try karo',
  'learn.chat.heading': 'Samajh nahi aa raha? Poocho',
  'learn.chat.placeholder': 'Kuch bhi poocho',
  'learn.chat.starterMeaning': 'Iska matlab kya hai?',
  'learn.chat.starterExample': 'Ek example do',
  'learn.chat.starterRemember': 'Isse kaise yaad rakhun?',
  'learn.chat.openHomework': 'Is sawaal ke liye Homework Help kholo',
  'learn.done': 'Ho gaya',
  'learn.empty': 'Koi subject chuno aur kuch naya seekhte hain.',

  'mastery.not_learned': 'Chalo ye seekhte hain',
  'mastery.learning': 'Tum ye seekh rahe ho',
  'mastery.practicing': 'Tum strong ho rahe ho',
  'mastery.mastered': 'Ye tumhe achhe se aata hai!',
  'mastery.retention_review': 'Chalo ise phir se dekhte hain',
  'mastery.none': 'Abhi shuru nahi kiya',

  'progress.title': 'Meri progress',
  'progress.empty': 'Jaise practice karoge, yahan tumhari journey dikhegi.',
  'progress.skillsStrong': 'Achha chal raha hai',
  'progress.skillsPractice': 'Thodi aur practice chahiye',

  'profile.title': 'Profile',
  'profile.class': 'Class',
  'profile.board': 'Board',
  'profile.language': 'Language',
  'profile.myProgress': 'Meri progress',
  'profile.account': 'Account',
  'profile.signOut': 'Sign out',
  'profile.signOutConfirm': 'PrepLift se sign out karna hai?',
  'profile.changeClassWarning':
    'Class badalne se tumhare sawaal aur lessons badal jayenge. Pakka?',
  'profile.confirm': 'Haan, badlo',
  'profile.cancel': 'Rehne do',
  'profile.saved': 'Save ho gaya',

  'language.en': 'English',
  'language.hinglish': 'Hinglish',
  'language.choose': 'Apni language chuno',

  'auth.welcome': 'PrepLift mein welcome',
  'auth.phonePlaceholder': '+91 98765 43210',
  'auth.sendLink': 'Mujhe link bhejo',
  'auth.sending': 'Bhej rahe hain...',
  'auth.linkSent': 'WhatsApp check karo',
  'auth.linkSentBody': 'Humne sign in ka link bheja hai. Ek baar chalega, 10 minute mein expire.',
  'auth.signingIn': 'Sign in kar rahe hain...',
  'auth.expired': 'Ye login link expire ho gaya. Chalo naya lete hain.',
  'auth.tryAgain': 'Naya link lo',

  'parent.title': 'Aapke bachche ki learning',
  'parent.thisWeek': 'Is hafte aapke bachche ki learning',
  'parent.homeworkCount': 'Homework: {count} sawaal',
  'parent.practiceCount': 'Practice: {count} sawaal',
  'parent.improving': 'Behtar ho raha hai',
  'parent.needsPractice': 'Aur practice chahiye',
  'parent.activity': 'Activity: is hafte {count} din',
  'parent.empty': 'Bachcha learning shuru karega to progress yahan dikhegi.',
  'parent.selectChild': 'Bachcha chuno',

  // --- parent-first onboarding ---------------------------------------------
  'profiles.heading': 'Kaun padh raha hai?',
  'profiles.parentTile': 'Parent ke roop mein chalo',
  'profiles.parentSubtitle': 'Progress, settings aur naya child',
  'profiles.addChild': 'Child add karo',
  'profiles.switch': 'Profile badlo',
  'profiles.empty': 'Shuru karne ke liye pehla child add karo.',
  'profiles.classLine': 'Class {class} · {board}',

  'auth.parentPhoneLabel': 'Aapka WhatsApp number',
  'auth.parentTagline': 'Do minute mein apne bachche ka account bana lijiye.',
  'parent.thisChild': 'aapka bachcha',
  'parent.link.heading': 'Unke phone par PrepLift kholna',
  'parent.link.none': 'WhatsApp par ek link bhejiye jo {name} ka PrepLift kholta hai. Number type karne ki zarurat nahi, aur ye chalta rehta hai.',
  'parent.link.send': 'WhatsApp par link bhejo',
  'parent.link.sending': 'Bhej rahe hain...',
  'parent.link.live': 'Link bhej diya. {date} tak chalega, aur jab tak {name} use karta rahega tab tak chalta rahega.',
  'parent.link.used': '{count} baar use hua',
  'parent.link.resend': 'Naya link bhejo',
  'parent.link.revoke': 'Link band karo',
  'parent.link.revoked': 'Wo link ab kaam nahi karta. Zarurat ho to naya bhej dijiye.',
  'parent.link.failed': 'WhatsApp tak abhi pahunch nahi paye. Dobara bhejne ki koshish kijiye.',
  'parent.link.caution': 'Jo bhi wo WhatsApp message kholega, {name} ka homework kar sakta hai. Usse aapki billing, reports ya settings nahi dikhti, aur aap kabhi bhi link band kar sakte hain.',
  'auth.recalledLabel': 'Isse sign in karo',
  'auth.recalledSend': 'Mera link bhejo',
  'auth.notMe': 'Dusra number use karo',
  'parent.setup.title': 'Apna account set kijiye',
  'parent.setup.nameLabel': 'Aapka naam',
  'parent.setup.namePlaceholder': 'jaise Priya',
  'parent.setup.pinTitle': 'Parent PIN chuniye',
  'parent.setup.pinWhy':
    'Bachcha isi phone par padhega. PIN se aap apni screen par wapas aayenge.',
  'parent.setup.pinLabel': 'PIN ({min}-{max} digit)',
  'parent.setup.pinConfirmLabel': 'Dobara likhiye',
  'parent.setup.pinMismatch': 'Dono PIN alag hain.',
  'parent.setup.save': 'Save karke aage badhiye',
  'parent.addChild.title': 'Apna bachcha add kijiye',
  'parent.addChild.nameLabel': 'Bachche ka naam',
  'parent.addChild.namePlaceholder': 'jaise Aarav',
  'parent.addChild.classLabel': 'Class',
  'parent.addChild.boardLabel': 'Board',
  'parent.addChild.languageLabel': 'Lesson ki language',
  'parent.addChild.consent':
    'Aap confirm karte hain ki aap is bachche ke parent ya guardian hain aur PrepLift unka learning data process kar sakta hai.',
  'parent.addChild.save': 'Bachcha add kijiye',
  'parent.addChild.another': 'Ek aur bachcha add kijiye',
  'parent.children.title': 'Aapke bachche',
  'parent.children.empty': 'Shuru karne ke liye apna bachcha add kijiye.',
  'parent.children.startPractice': 'Phone bachche ko dijiye',
  'parent.children.ownLogin': 'Apne phone par apne number se sign in karte hain.',
  'parent.children.openApp': '{name} ka app kholo',
  'parent.children.openAppUnnamed': 'Unka app kholo',
  'parent.children.seeProgress': 'Progress dekhiye',
  'parent.handoff.confirmTitle': '{name} ko phone de rahe hain?',
  'parent.handoff.confirmBody':
    'Aap yahan se sign out ho jayenge. Wapas aane ke liye "Parent zone" par apna PIN daaliye.',
  'parent.handoff.confirm': 'Phone dijiye',
  'parent.handoff.needsPin': 'Pehle parent PIN set kijiye, taaki wapas aa sakein.',
  'parent.zone': 'Parent zone',
  'parent.section.title': 'Parent ke liye',
  'parent.section.changePin': 'PIN badlo',
  'start.title': 'Phone kaun use kar raha hai?',
  'start.whose': 'Naam par tap karke unka PrepLift kholiye.',
  'start.open': 'Kholo',
  'pin.label': 'Parent PIN',
  'pin.body': 'Papa ya mummy se PIN daalne ko kahiye.',
  'pin.submit': 'Aage badho',
  'pin.noneSet': 'Is account par abhi PIN set nahi hai. WhatsApp se sign in karke set kar lijiye.',
  'parent.unlock.title': 'Apna PIN daaliye',
  'parent.unlock.body': 'Isse aap parent screen par wapas aa jayenge.',
  'parent.unlock.submit': 'Unlock',
  'parent.unlock.attempts': '{count} try bache hain',
  'parent.unlock.signOutInstead': 'WhatsApp se sign in kijiye',
  'child.practisingAs': 'Tum {name} ke roop mein practice kar rahe ho',

  'error.OFFLINE': 'Lagta hai internet nahi hai. Check karke phir try karo.',
  'error.NETWORK_ERROR': 'Lagta hai internet nahi hai. Check karke phir try karo.',
  'error.TIMEOUT': 'Bahut time lag gaya. Chalo phir se try karte hain.',
  'error.AI_UNAVAILABLE': 'Abhi complete nahi kar paaye. Chalo phir se try karte hain.',
  'error.AI_SCHEMA_REJECTED': 'Abhi complete nahi kar paaye. Chalo phir se try karte hain.',
  'error.AI_SAFETY_REJECTED': 'Is sawaal mein help nahi kar sakta. Koi homework sawaal try karo.',
  'error.AI_QUOTA_EXCEEDED': 'Aaj bahut kaam kar liya! Baad mein try karo.',
  'error.HOMEWORK_PROCESSING_FAILED': 'Abhi complete nahi kar paaye. Chalo phir se try karte hain.',
  'error.PAYLOAD_TOO_LARGE': 'Ye photo bahut badi hai. Chhoti wali try karo.',
  'error.UNSUPPORTED_MEDIA_TYPE': 'Ye wala nahi khul raha. Dobara try karo?',
  'error.TOKEN_EXPIRED': 'Ye login link expire ho gaya. Chalo naya lete hain.',
  'error.TOKEN_ALREADY_USED': 'Ye link pehle use ho chuka hai. Chalo naya lete hain.',
  'error.SESSION_REVOKED': 'Tum sign out ho gaye. Chalo phir se sign in karte hain.',
  'error.AUTHENTICATION_REQUIRED': 'Continue karne ke liye sign in karo.',
  'error.INVALID_CREDENTIALS': 'Ye link kaam nahi kiya. Chalo naya lete hain.',
  'error.PERMISSION_DENIED': 'Iska access tumhare paas nahi hai.',
  'error.SUBSCRIPTION_REQUIRED': 'Tumhara free trial khatam ho gaya. Parent se plan continue karne ko kaho.',
  'error.PIN_INCORRECT': 'Ye PIN sahi nahi hai.',
  'error.PIN_LOCKED': 'Bahut baar try ho gaya. Thodi der baad koshish kijiye.',
  'error.PIN_TOO_WEAK': 'Thoda kam aasaan PIN chuniye.',
  'error.RESOURCE_NOT_FOUND': 'Ye mila nahi.',
  'error.RATE_LIMITED': 'Thoda fast ho gaya. Saans lo aur phir try karo.',
  'error.CURRICULUM_NOT_AVAILABLE': 'Ye syllabus abhi ready nahi hai.',
  'error.PRACTICE_SESSION_CLOSED': 'Ye practice set already complete hai.',
  'error.MALFORMED_RESPONSE': 'Kuch galat ho gaya. Phir se try karo.',
  'error.unknown': 'Kuch galat ho gaya. Phir se try karo.',
  'error.retry': 'Phir se try karo',
  'error.goHome': 'Home jao',
  'error.uploadFailed': 'Photo upload nahi hui. Phir se try karo.',
  'error.notFoundTitle': 'Ye page nahi mila',
  'error.notFoundBody': 'Chalo tumhe kahin kaam ki jagah le chalte hain.',

  'legal.privacy': 'Privacy Policy',
  'legal.terms': 'Terms of Use',
  'legal.refunds': 'Cancellation and Refunds',
  'legal.effective': '{date} se laagu',
  'legal.otherDocuments': 'Baaki policies',
  'legal.backToApp': 'PrepLift par wapas',

  'common.loading': 'Load ho raha hai...',
  'common.back': 'Peeche',
  'common.close': 'Band karo',
  'common.continue': 'Aage badho',
  'common.cancel': 'Cancel',

  'pwa.installTitle': 'PrepLift ko home screen par lagao',
  'pwa.installBody': 'Browser ke bina, app ki tarah kholo.',
  'pwa.install': 'Add karo',
  'pwa.notNow': 'Abhi nahi',
  'pwa.updateTitle': 'Naya version ready hai',
  'pwa.update': 'Refresh karo',
};

const DICTIONARIES: Record<Language, Record<MessageKey, string>> = { en, hinglish };

/**
 * Look up a key and interpolate {placeholders}.
 *
 * A missing key returns the key itself rather than throwing or rendering
 * blank: a child should see something odd, not an empty screen, and the odd
 * string is obvious in QA.
 */
export function translate(
  key: MessageKey,
  language: Language,
  values?: Record<string, string | number>,
): string {
  const template = DICTIONARIES[language][key] ?? en[key] ?? key;
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match,
  );
}

/** Child-facing copy for an error code, falling back to the generic line. */
export function errorMessage(code: string, language: Language): string {
  const key = `error.${code}` as MessageKey;
  if (key in en) return translate(key, language);
  return translate('error.unknown', language);
}

/** Mastery state → words, never a number (§27). */
export function masteryLabel(state: string | null | undefined, language: Language): string {
  const key = `mastery.${state ?? 'none'}` as MessageKey;
  if (key in en) return translate(key, language);
  return translate('mastery.none', language);
}
